import { Bell, CheckCheck, ExternalLink, Inbox } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../src/hooks/useAuth';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllRead,
  type InAppNotification,
} from '../../src/hooks/queries/use-notifications';
import { useAppNav } from '../../src/contexts/app-nav.context';
import { formatDistanceToNow } from 'date-fns';

const TYPE_LABELS: Record<string, string> = {
  project_assigned: 'Project',
  task_assigned: 'Task',
  daily_log_submitted: 'Daily Log',
  rfi_assigned: 'RFI',
  submittal_review: 'Submittal',
  punchlist_assigned: 'Punch List',
  incident_assigned: 'Safety',
  po_status_changed: 'Purchase Order',
  grn_created: 'Goods Received',
  material_requisition: 'Requisition',
  general: 'General',
};

const TYPE_COLORS: Record<string, string> = {
  project_assigned: 'bg-blue-100 text-blue-800',
  task_assigned: 'bg-purple-100 text-purple-800',
  daily_log_submitted: 'bg-green-100 text-green-800',
  rfi_assigned: 'bg-yellow-100 text-yellow-800',
  submittal_review: 'bg-orange-100 text-orange-800',
  punchlist_assigned: 'bg-red-100 text-red-800',
  incident_assigned: 'bg-red-200 text-red-900',
  po_status_changed: 'bg-indigo-100 text-indigo-800',
  grn_created: 'bg-teal-100 text-teal-800',
  material_requisition: 'bg-cyan-100 text-cyan-800',
  general: 'bg-gray-100 text-gray-800',
};

function timeAgo(iso: string) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

function NotificationRow({
  notif,
  onRead,
  onNavigate,
}: {
  notif: InAppNotification;
  onRead: (id: string) => void;
  onNavigate: (url: string) => void;
}) {
  const label = TYPE_LABELS[notif.notification_type] ?? 'General';
  const color = TYPE_COLORS[notif.notification_type] ?? TYPE_COLORS.general;

  const handleClick = () => {
    if (!notif.is_read) onRead(notif.id);
    if (notif.action_url) onNavigate(notif.action_url);
  };

  return (
    <div
      className={`flex items-start gap-3 p-4 border-b last:border-b-0 transition-colors cursor-pointer hover:bg-muted/40 ${
        !notif.is_read ? 'bg-primary/5' : ''
      }`}
      onClick={handleClick}
    >
      {!notif.is_read && (
        <span className="mt-2 h-2 w-2 rounded-full bg-primary flex-shrink-0" />
      )}
      {notif.is_read && <span className="mt-2 h-2 w-2 flex-shrink-0" />}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
            {label}
          </span>
          <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">
            {timeAgo(notif.created_at)}
          </span>
        </div>
        <p className="text-sm font-medium leading-snug">{notif.title}</p>
        <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
      </div>

      {notif.action_url && (
        <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
      )}
    </div>
  );
}

export function NotificationsPage() {
  const { user } = useAuth();
  const userId = (user as any)?.id ?? (user as any)?.sub ?? null;
  const { data: notifications = [], isLoading } = useNotifications(userId);
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markAllRead } = useMarkAllRead();
  const { navigate } = useAppNav();

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleNavigate = (url: string) => {
    navigate(url);
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          <h2 className="text-xl font-semibold">Notifications</h2>
          {unreadCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {unreadCount} unread
            </Badge>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllRead()}
            className="gap-1"
          >
            <CheckCheck className="h-4 w-4" />
            Mark all read
          </Button>
        )}
      </div>

      <Card>
        {isLoading ? (
          <CardContent className="py-12 text-center text-muted-foreground text-sm">
            Loading notifications…
          </CardContent>
        ) : notifications.length === 0 ? (
          <CardContent className="py-16 flex flex-col items-center gap-3 text-muted-foreground">
            <Inbox className="h-12 w-12 opacity-40" />
            <p className="text-sm">You're all caught up — no notifications yet.</p>
          </CardContent>
        ) : (
          <>
            {notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notif={n}
                onRead={(id) => markRead(id)}
                onNavigate={handleNavigate}
              />
            ))}
          </>
        )}
      </Card>
    </div>
  );
}
